import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import PersonalWishlist from './PersonalWishlist'
import {
  awaitPendingJob,
  displayErrorToast,
  getErrorDetails,
  imageUrl,
} from 'app/frontend/base/helpers'
import { Check, CircleCheck } from 'lucide-react'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { LootContext, PersonalLootContext } from '../context'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import LoginButtons from 'app/frontend/base/components/LoginButtons'
import AppContext from 'app/frontend/base/config/context/app'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { useTranslation } from 'react-i18next'
import {
  manuallyUpdateWishlist,
  uploadWishlistReport,
} from 'app/frontend/base/api/character'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { EditableWish } from '../types'
import { toast } from 'sonner'
import OptionBar from './OptionBar'
import Select from 'app/frontend/base/components/Select'
import groupBy from 'lodash/groupBy'
import { flatten } from 'lodash'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import RcLootCouncilCta from './RcLootCouncilCta'
import NoMemberWarning from './NoMemberWarning'
import Card from 'app/frontend/base/components/Card'

const PersonalOverview = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { characterId } = useParams()
  const { team } = useTeam()
  const {
    data: {
      metadata: { droptimizerUpgradeLevels },
      user,
    },
  } = usePageInfo()
  const { currentCharacterRef } = useContext(AppContext)
  const {
    difficulty,
    setDifficulty,
    selectedSeason,
    configuration,
    droptimizerConfigurations,
    setConfiguration,
  } = useContext(LootContext)
  const { accessLevel } = useAuth()
  const { t } = useTranslation()

  const availableMembers = useMemo(
    () =>
      team.members?.filter(
        (member) => user && member.characterReference.userId === user?.id,
      ),
    [team, user],
  )

  const setDefaultSelectedCharacter = () =>
    team.members?.find((member) =>
      characterId
        ? member.characterReference.id === parseInt(characterId)
        : currentCharacterRef
        ? member.characterReference.id === currentCharacterRef.id
        : member.characterReference.userId === user?.id,
    )?.characterReference ??
    (characterId ? undefined : availableMembers?.[0]?.characterReference)

  const [changes, setChanges] = useState<Record<string, EditableWish>>({})
  const [reportId, setReportId] = useState('')
  const [clearExisting, setClearExisting] = useState(true)
  const [selectedCharacter, setSelectedCharacter] = useState(
    setDefaultSelectedCharacter(),
  )
  const [editing, setEditing] = useState(false)
  const [submittingReport, setSubmittingReport] = useState(false)
  const [reportsUploaded, setReportsUploaded] = useState(0)
  const [error, setError] = useState<string>()
  const [editError, setEditError] = useState<string>()
  const wishlistLocked =
    team.settings.wishlistLocked.value === true && accessLevel !== 'admin'
  const wishlistNotEditable = team.settings.wishlistEditable.value === false

  useEffect(() => {
    setSelectedCharacter(setDefaultSelectedCharacter())
  }, [team.members?.length])

  const handleSubmitReport = async () => {
    if (!selectedCharacter) return

    setError(undefined)
    setEditing(false)
    setChanges({})
    setSubmittingReport(true)

    uploadWishlistReport(
      team.id,
      selectedCharacter.id,
      reportId,
      configuration.id,
      clearExisting,
    )
      .then(async (response) => {
        awaitPendingJob<{
          newData: wowaudit.CharacterWishlist
          newDifficulty: Difficulty
          teamsUploaded: number
        }>(response.job_id, async (result) => {
          if (result.error) {
            setError(result.error)
          } else if (result.content?.teamsUploaded) {
            await queryClient.setQueryData(
              [
                'characterWishlist',
                {
                  teamId: team.id,
                  characterId: selectedCharacter.id,
                  seasonId: selectedSeason.id,
                },
              ],
              result.content?.newData,
            )

            setDifficulty(result.content?.newDifficulty || difficulty)
            setReportsUploaded(result.content?.teamsUploaded || 0)
          }

          setSubmittingReport(false)
        })
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmittingReport(false)
        setError(
          'Something went wrong submitting the report. Please try again.',
        )
      })
  }

  const manualUpdateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCharacter) return

      return await manuallyUpdateWishlist(
        team.id,
        selectedCharacter.id,
        selectedSeason.id,
        changes,
      )
    },
    onSuccess: async (newData) => {
      await queryClient.setQueryData(
        [
          'characterWishlist',
          {
            teamId: team.id,
            characterId: selectedCharacter?.id,
            seasonId: selectedSeason.id,
          },
        ],
        newData,
      )

      queryClient.invalidateQueries({
        queryKey: ['teamWishlist'],
      })

      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => {
      setEditError(
        getErrorDetails(error, t).description ??
          'Something went wrong. Please try again.',
      )
    },
    onSettled: () => {
      setEditing(false)
      setChanges({})
    },
  })

  const formatReportId = (reportId: string) => {
    const formatted = reportId
      .replace('raidbots.com/simbot/report/', '')
      .replace('https://', '')
      .replace('www.', '')
      .replace('.json', '')
      .replace('questionablyepic.com/api/upgrades/', '')
      .replace('questionablyepic.com/live/upgradereport/', '')
      .replace('questionablyepic.com/live/report/', '')

    return formatted
  }

  const memberOptions = useMemo(() => {
    return flatten(
      Object.entries(
        groupBy(
          (team.members ?? [])
            .filter((member) => member.teamRank?.wishlist_visibility)
            .sort((a, b) =>
              a.characterReference.name.localeCompare(
                b.characterReference.name,
              ),
            ),
          (member) =>
            user && member.characterReference.userId === user?.id
              ? 'My characters'
              : 'Other characters',
        ),
      ).map(([category, members]) =>
        members.map((member) => ({
          category,
          value: member.characterReference.id.toString(),
          customOptionFn: (isSelected: boolean) => (
            <div className='flex items-center gap-2.5 w-full whitespace-nowrap'>
              <div className='grow flex items-center gap-1.5'>
                <CharacterClassIcon
                  size='small'
                  characterClass={member.characterReference.characterClass}
                />
                <CharacterRoleIcon size='small' role={member.role} />
                {member.characterReference.name}
              </div>
              <div className={isSelected ? 'opacity-100' : 'opacity-0'}>
                <Check size={16} />
              </div>
            </div>
          ),
          label: (
            <div className='flex items-center gap-2'>
              <CharacterClassIcon
                characterClass={member.characterReference.characterClass}
                size='medium'
              />
              {member.characterReference.name}
            </div>
          ),
        })),
      ) ?? [],
    )
  }, [team.members])

  const readonly =
    accessLevel !== 'admin' &&
    !availableMembers?.find(
      (member) => member.characterReference.id === selectedCharacter?.id,
    )

  if (memberOptions?.length === 0) {
    return <NoMemberWarning />
  }

  return (
    <div>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>
          Wishlist for
          <Select
            className='ml-2 w-fit text-2xl px-2 font-bold'
            value={selectedCharacter?.id.toString()}
            separateCategories={true}
            placeholder='character'
            options={memberOptions}
            optionWidth='fit'
            setValue={(value) => {
              navigate(`${team.path}/loot/characters/${value}`)
              setReportsUploaded(0)
              setReportId('')
              setSelectedCharacter(
                team.members?.find(
                  (member) => member.characterReference.id.toString() === value,
                )?.characterReference,
              )
            }}
          />
        </div>
        <OptionBar>
          {droptimizerConfigurations.length > 1 && (
            <Select
              className='w-48'
              value={configuration.id.toString()}
              options={droptimizerConfigurations.map((configuration) => ({
                value: configuration.id.toString(),
                label: configuration.name,
                description: configuration.description,
              }))}
              setValue={(value) =>
                setConfiguration(
                  droptimizerConfigurations.find(
                    (configuration) => configuration.id.toString() === value,
                  ) as wowaudit.DroptimizerConfiguration,
                )
              }
            />
          )}
        </OptionBar>
      </h2>

      {readonly ? null : (
        <Card className='pt-5 px-6 pb-6 flex gap-4 max-md:flex-col min-h-66'>
          <div className='grow basis-1/2'>
            <h4 className='flex items-center gap-2'>
              Upload{' '}
              <a
                href={`https://www.raidbots.com/simbot/droptimizer?region=${selectedCharacter?.realm.region}&realm=${selectedCharacter?.realm.slug}&name=${selectedCharacter?.name}`}
                target='_blank'
                rel='noreferrer'
                className='bg-black py-1 px-2 rounded-md border dark:border-0 dark:p-0 dark:bg-transparent'
              >
                <img
                  className='h-6 w-[6.6rem]'
                  title='Raidbots'
                  src={imageUrl('raidbots-logo.svg')}
                />
              </a>
              or
              <a
                href={`https://questionablyepic.com/live/UpgradeFinder`}
                target='_blank'
                rel='noreferrer'
                className='bg-black py-1 px-2 rounded-md border dark:border-0 dark:p-0 dark:bg-transparent'
              >
                <img
                  className='h-6'
                  title='QE Live'
                  src={imageUrl('qe-logo.png')}
                />
              </a>
              report
            </h4>

            {reportsUploaded > 0 ? (
              <div className='flex flex-col justify-center items-center gap-4 mt-12'>
                <CircleCheck size={60} className='text-success-foreground' />
                <span>
                  Your report has been uploaded
                  {reportsUploaded > 1
                    ? ` (also to ${reportsUploaded - 1} other team${
                        reportsUploaded === 2 ? '' : 's'
                      }). `
                    : '. '}
                  <a
                    className='text-primary hover:text-primary/90 cursor-pointer'
                    onClick={() => {
                      setReportsUploaded(0)
                      setReportId('')
                    }}
                  >
                    Upload another
                  </a>
                  .
                </span>
              </div>
            ) : (
              <React.Fragment>
                <p className='mt-2 mb-4'>
                  Run the{' '}
                  <a
                    className='text-primary hover:text-primary/90'
                    href={`https://www.raidbots.com/simbot/droptimizer?region=${selectedCharacter?.realm.region}&realm=${selectedCharacter?.realm.slug}&name=${selectedCharacter?.name}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Raidbots Droptimizer
                  </a>{' '}
                  or the{' '}
                  <a
                    target='_blank'
                    rel='noreferrer'
                    className='text-primary hover:text-primary/90'
                    href='https://questionablyepic.com/live/UpgradeFinder'
                  >
                    QE Live Upgrade Finder
                  </a>{' '}
                  for your character and paste the URL here.
                </p>

                <div className='flex relative'>
                  <Input
                    type='text'
                    className='text-center text-[1.25rem] border-r-0 pr-16'
                    disabled={
                      submittingReport ||
                      wishlistLocked ||
                      availableMembers?.length === 0
                    }
                    value={reportId}
                    autoComplete='off'
                    autoCorrect='off'
                    spellCheck='false'
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && reportId) {
                        handleSubmitReport()
                      }
                    }}
                    onFocus={(event) => event.target.select()}
                    onChange={(event) =>
                      setReportId(formatReportId(event.target.value))
                    }
                  />
                  <div className='absolute right-0'>
                    <Button
                      onClick={handleSubmitReport}
                      loading={submittingReport}
                      spinner='clip'
                      disabled={
                        !reportId ||
                        wishlistLocked ||
                        availableMembers?.length === 0
                      }
                      className='rounded-l-none'
                    >
                      <span>Go</span>
                    </Button>
                  </div>
                </div>

                <div className='text-xs text-muted-foreground mt-1.5'>
                  {wishlistLocked ? (
                    <span className='text-warning-foreground'>
                      All wishlists are currently locked for your team.
                    </span>
                  ) : (
                    <span>
                      {configuration.description}
                      {`, ${
                        droptimizerUpgradeLevels[difficulty].find(
                          (o) =>
                            o.value ===
                            configuration[
                              `upgradeLevel${
                                (
                                  {
                                    mythic: '',
                                    heroic: 'Heroic',
                                    normal: 'Normal',
                                    raid_finder: 'Lfr',
                                  } as const
                                )[difficulty]
                              }`
                            ],
                        )?.label || 'unknown'
                      } upgrades`}
                    </span>
                  )}
                  {error && (
                    <div className='text-destructive-foreground'>{error}</div>
                  )}
                </div>

                <div className='flex items-center gap-2 mt-3'>
                  <Checkbox
                    id='replace-manual-edits'
                    checked={clearExisting}
                    onCheckedChange={() => setClearExisting(!clearExisting)}
                    disabled={wishlistLocked || availableMembers?.length === 0}
                  />
                  <label
                    htmlFor='replace-manual-edits'
                    className='cursor-pointer'
                  >
                    Replace manual edits
                  </label>
                </div>
              </React.Fragment>
            )}
          </div>

          <div className="relative w-0.5 max-md:h-0.5 my-0 mx-4 bg-border after:absolute after:content-['or'] after:h-8 after:w-8 after:flex after:justify-center after:items-center after:rounded-full after:-left-4 after:top-[calc(50%-1rem)] after:bg-card max-md:w-full max-md:m-0 max-md:my-2 max-md:after:left-[calc(50%-1rem)]" />

          <div className='grow basis-1/2'>
            <h4>Edit manually</h4>
            <p className='mt-2 mb-4'>
              You can configure your entire wishlist manually, or adjust the
              result of the Droptimizer report. Manual entries will be shown
              differently in the overview.
            </p>
            {editing ? (
              <div className='flex gap-2'>
                <Button
                  className='w-32'
                  onClick={() => {
                    setEditing(false)
                    setChanges({})
                  }}
                  variant='outline'
                  disabled={manualUpdateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => manualUpdateMutation.mutate()}
                  loading={manualUpdateMutation.isPending}
                  disabled={manualUpdateMutation.isPending}
                >
                  Save changes
                </Button>
              </div>
            ) : (
              <Button
                className='w-32'
                onClick={() => setEditing(true)}
                variant='outline'
                disabled={
                  wishlistLocked ||
                  wishlistNotEditable ||
                  availableMembers?.length === 0
                }
              >
                Start editing
              </Button>
            )}
            <div className='warnings'>
              {editError && (
                <p className='mt-2 text-destructive-foreground'>{editError}</p>
              )}
              {wishlistNotEditable && !wishlistLocked && (
                <p className='mt-2 text-warning-foreground'>
                  This option has been disabled for your team.
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {!selectedCharacter ? (
        <div className='mt-4'>
          {characterId ? (
            <Alert>
              <AlertTitle>Character not found</AlertTitle>
              <AlertDescription>
                <p>
                  We could not find the character you are looking for. Please
                  select another character.
                </p>
              </AlertDescription>
            </Alert>
          ) : user ? (
            <div>
              {user.characterReferences.length === 0 ? (
                <Alert>
                  <AlertTitle>No characters found</AlertTitle>
                  <AlertDescription>
                    <p className='mb-4'>
                      We could not find any of your characters. Please visit
                      your <Link to='/profile/characters'>profile</Link> and
                      update your list of characters.
                    </p>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <AlertTitle>No characters in team</AlertTitle>
                  <AlertDescription>
                    <p>
                      You do not have any characters in this team. Please reach
                      out to your team&apos; administrator, or visit your{' '}
                      <Link to='/profile/characters'>profile</Link> and update
                      your list of characters.
                    </p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <Alert>
              <AlertTitle>Not logged in</AlertTitle>
              <AlertDescription>
                <p className='mb-4'>
                  Please log in to upload wishlist information for your
                  characters in this team.
                </p>
                <LoginButtons withLabel={false} />
              </AlertDescription>
            </Alert>
          )}
        </div>
      ) : null}

      {selectedCharacter && (
        <PersonalLootContext.Provider
          value={{
            characterReference: selectedCharacter,
            editing,
            setEditing,
            changes,
            setChanges,
          }}
        >
          <RcLootCouncilCta />

          <PersonalWishlist />
        </PersonalLootContext.Provider>
      )}
    </div>
  )
}

export default PersonalOverview
